import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageModal, Loading, WirecardCancelForm } from '../Main'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import {
  currencyFormat,
  formatDate,
  prettyUrl,
  numberFormatAPI,
  checkValidationFile,
  formatDecimal,
  currencyFormatDecimal,
} from '../../utility/Helpers'
import {
  InteractorOrderUpdate,
  InteractorDownloadPdf,
  InteractorUpdateImage,
  InteractorBillUpdate,
  InteractorCorrectionSearch,
} from '../../interactors/Main'
import { Modal } from 'react-responsive-modal'
import pdfLogo from '../../assets/image/icons/pdfLogo.png'
import { OrderPurchaseNumberForm } from '../../containers/Main'
import { FindFileType } from '../../utility/Helpers'
import { confirmationAlert, showAlert } from '../../utility/SweetAlert'
import Compressor from 'compressorjs'
import emptyImage from '../../assets/image/skeleton/product.png'
import { INVOICE_V2 } from '../../constant/global/config'
import moment from 'moment'
import WideCardOrderReturn from './WideCardOrderReturn'
import WideCardBankTransferPayment from './WideCardBankTransferPayment'

const WideCardOrder = (data) => {
  const pathSegments = window.location.pathname.split('/')
  const uid = pathSegments[pathSegments.length - 1]
  const { loading, datacard, onCloseOrder, onCorrectionChange } = data
  const { t } = useTranslation()
  const { account } = useSelector((state) => state.account)
  const location = useLocation()
  const { uploadImage } = InteractorUpdateImage()
  const { billUpdate } = InteractorBillUpdate()
  const { correctionSearch } = InteractorCorrectionSearch()
  const { orderUpdate } = InteractorOrderUpdate()
  const { getInvoice, getTransaction } = InteractorDownloadPdf()
  const [modalPrOpen, setModalPrOpen] = useState(false)
  const [modalReturOpen, setModalReturOpen] = useState(false)
  const [modalReturnData, setModalReturnData] = useState({})
  const [modalBankTransferOpen, setModalBankTransferOpen] = useState(false)
  const [modalBankTransferData, setModalBankTransferData] = useState({})
  const [loadingCorrection, setLoadingCorrection] = useState(true)
  const [modalPoOpen, setModalPoOpen] = useState(false)
  const [error, setError] = useState(null)
  const [isAnyRequestCorrection, setIsAnyRequestCorrection] = useState(false)
  const [isModalRejected, setIsModalRejected] = useState(false)
  const [payloadRejected, setPayloadRejected] = useState(null)
  const invoiceVersionDate = new Date(INVOICE_V2)
  const [deliveryReceiptFile, setDeliveryReceiptFile] = useState({
    proof_1: null,
    proof_2: null,
    proof_3: null,
    proof_4: null,
    proof_5: null,
  })
  const payloadCorrectionSearch = useMemo(
    () => ({
      page: 1,
      limit: 50,
      keywords: {
        text_search: '',
        status: '',
        account_uid: '',
        store_uid: '',
        uid: '',
        transaction_uid: uid,
      },
    }),
    [uid],
  )
  const handleModalPr = () => {
    setModalPrOpen(false)
  }
  const handleModalPo = () => {
    setModalPoOpen(false)
  }

  const handleUploadFile = (event, billData) => {
    const { files, name } = event.target
    const redirectLink = location.pathname
    let file = files[0]
    let isValidImage = checkValidationFile(file.type, file.size)

    if (!isValidImage) {
      setError((prev) => ({ ...prev, [name]: t('message.invalid_picture') }))
      return
    }
    if (file.type === 'application/pdf') {
      const payload = {
        type: 'PUBLIC',
        category: 'ORDER_PROOF_PAYMENT',
        file: new File([file], `${Date.now().toString()}.${file.name.split('.').pop()}`, {
          type: file.name.split('.').pop(),
        }),
        reference_uid: account.uid,
        updated_by: account.full_name,
      }
      uploadImage(payload, (response) => {
        delete billData.bill_items
        billUpdate(
          {
            ...billData,
            file_proof_payment: response,
            status: 'PENDING_APPROVAL',
            updated_by: account.full_name,
          },
          redirectLink,
        )
      })
    } else {
      new Compressor(file, {
        quality: 0.8,
        width: 300,
        height: 300,
        convertSize: 200000,
        success: async (compressedResult) => {
          const payload = {
            type: 'PUBLIC',
            category: 'ORDER_PROOF_PAYMENT',
            file: new File(
              [compressedResult],
              `${Date.now().toString()}.${compressedResult.name.split('.').pop()}`,
              {
                type: compressedResult.name.split('.').pop(),
              },
            ),
            reference_uid: account.uid,
            updated_by: account.full_name,
          }
          uploadImage(payload, (response) => {
            delete billData.bill_items
            billUpdate(
              {
                ...billData,
                file_proof_payment: response,
                status: 'PENDING_APPROVAL',
                updated_by: account.full_name,
              },
              redirectLink,
            )
          })
        },
      })
    }
    setError((prev) => ({ ...prev, [name]: null }))
  }

  const handleTransactionUpdate = async (row, status) => {
    let payloadUpdate = { ...row }
    payloadUpdate.status = status
    payloadUpdate.updated_by = account.full_name
    payloadUpdate.delivery_estimated_time = formatDate(
      row.delivery_estimated_time,
      'YYYY-MM-DD HH:mm:ss',
    )
    if (row?.payment_term_date) {
      payloadUpdate.payment_term_date = formatDate(
        new Date(row.payment_term_date),
        'YYYY-MM-DD HH:mm:ss',
      )
    }
    const filteredProofs = Object.entries(deliveryReceiptFile)
      .filter(([_, value]) => value !== null) // Keep only non-null values
      .join(', ') // Join keys with ", "
    payloadUpdate.delivery_receipt_file = filteredProofs
    if (row.payment_status === 'UNPAID' && status === 'COMPLETED') {
      showAlert({
        icon: 'warning',
        message: t('message.disallow_order_completed'),
        reload: false,
      })
    } else {
      if (status === 'COMPLETED' || status === 'DELIVERED') {
        orderUpdate(payloadUpdate)
      } else {
        setIsModalRejected(true)
        setPayloadRejected(payloadUpdate)
      }
    }
  }

  const handleOrderRejected = async (value) => {
    const reject = await confirmationAlert({
      title: t('title.are_you_sure'),
      message: t('message.confirmation_order_cancel'),
      icon: 'warning',
    })
    if (reject) {
      orderUpdate(value, 'REJECTED')
    }
  }

  const handleDownloadPdf = (data) => {
    getTransaction(data)
  }

  const handleDownloadInvoice = (row) => {
    const payload = {
      ...row,
      purchase_request_number: row.purchase_request_number,
      purchase_order_number: row.purchase_order_number,
    }

    if (
      datacard?.items[0]?.created_date &&
      new Date(datacard?.items[0]?.created_date) > invoiceVersionDate
    ) {
      delete payload.delivery_items
    } else {
      delete payload.bill_items
    }

    getInvoice(payload)
  }

  const handleResponse = useCallback(
    (data) => {
      const items = data?.items || []
      const res = items.filter((item) => item.initial_status === 'REQUEST')
      const hasRequest = res?.length > 0
      const count = res?.length

      onCorrectionChange({ items, hasRequest, count })

      if (hasRequest) {
        setIsAnyRequestCorrection(true)
      } else {
        setIsAnyRequestCorrection(false)
      }
    },
    [onCorrectionChange],
  )

  useEffect(() => {
    if (!loadingCorrection) return

    correctionSearch(payloadCorrectionSearch, handleResponse, setLoadingCorrection)
  }, [loadingCorrection, correctionSearch, payloadCorrectionSearch, handleResponse])

  useEffect(() => {
    if (!loading) {
      const hash = window.location.hash // Get the hash from the URL
      if (datacard.items?.length > 0 && datacard.items[0].delivery_receipt_file) {
        let proof = datacard?.items[0]?.delivery_receipt_file
        let pairs = proof.split(', ') // Split by ', ' to get key-value pairs
        pairs = pairs.map((item) => item.split(','))
        const result = pairs.reduce((acc, [key, value]) => {
          acc[key] = value
          return acc
        }, {})
        setDeliveryReceiptFile((prevState) => {
          return { ...prevState, ...result }
        })
      }
      if (hash) {
        const element = document.querySelector(hash) // Select the element by hash
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' }) // Smooth scrolling
        }
      }
    }
  }, [datacard, loading]) // Run only on component mount

  // CARD AREA
  const CardHeader = (data) => {
    const row = data.data

    return (
      <>
        <div className="row">
          <div className="col-8 mt-2">
            <span className="d-inline-block p-2">
              <small>{t('title.order_number')}</small> <br />
              <strong className="font-size-16">{row.transaction_code}</strong>
            </span>
            <div
              role="button"
              onClick={() => navigator.clipboard.writeText(row?.transaction_code)}
              className="ml-2 fa fa-copy btn-link border-0 bg-transparent pointer"
            ></div>
          </div>
          <div className="col-4 mt-1 d-flex justify-content-end">
            <span className="d-inline-block p-2">
              <small>{t('title.status_transaction')}: </small> <br />
              <span
                className={`font-weight-bold font-size-16 badge badge-bigger ${
                  ['WAITING_PAYMENT', 'PROCESSED', 'SHIPPED'].includes(row.status)
                    ? 'badge-pending'
                    : ''
                }${['DELIVERED', 'COMPLETED'].includes(row.status) ? 'badge-success' : ''}${
                  ['CANCELLED', 'REFUND'].includes(row.status) ? 'badge-reject' : ''
                }`}
              >
                {t(`initial_status.${row.status}`)}
              </span>
            </span>
          </div>
        </div>
        <hr className="m-0" />
      </>
    )
  }

  const CardBody = (data) => {
    const row = data?.data
    const bill = row?.bill && typeof row.bill === 'string' ? JSON.parse(row.bill) : []

    const items = row?.transaction_items
    let subtotal = 0
    let subweight = 0
    const handleUploadPicture = (event, source) => {
      const { files, name } = event.target
      let image = files[0]
      let isValidImage = checkValidationFile(image.type, image.size)

      if (!isValidImage) {
        setError((prev) => ({ ...prev, [name]: t('message.invalid_picture') }))
        // setDeliveryReceiptFile({
        // ...profile,
        // profile_picture: null,
        // })
        return
      }
      new Compressor(image, {
        quality: 0.8,
        width: 300,
        height: 300,
        convertSize: 200000,
        success: async (compressedResult) => {
          const payload = {
            type: 'PUBLIC',
            category: 'DELIVERY_PROOF',
            file: new File(
              [compressedResult],
              `${Date.now().toString()}.${compressedResult.name.split('.').pop()}`,
              {
                type: compressedResult.name.split('.').pop(),
              },
            ),
            reference_uid: account.uid,
            updated_by: account.full_name,
          }
          uploadImage(payload, (response) => {
            setDeliveryReceiptFile((prevState) => {
              return {
                ...prevState,
                [source]: response,
              }
            })
          })
        },
      })
      setError((prev) => ({ ...prev, [name]: null }))
    }
    const DeliveryProof = (data) => {
      const { proof } = data
      return (
        <>
          <div className="col-sm-2 p-0 ">
            <div className="container card-profile">
              <div
                className={`form-picture w100 border  ${
                  error?.delivery_proof ? 'border-danger' : ''
                }`}
              >
                <img
                  className="form-picture-image width-100 width-100-sm"
                  src={deliveryReceiptFile[proof] ? deliveryReceiptFile[proof] : emptyImage}
                  width="100"
                  height="100"
                  alt=""
                />
                {!['COMPLETED', 'DELIVERED'].includes(row.status) && (
                  <div className="form-picture-content pointer">
                    <span className="form-picture__icon">
                      <i className="fas fa-camera"></i>
                    </span>
                    <span className="form-picture__text">{t('placeholder.upload_image')}</span>
                    <input
                      type="file"
                      className="input-file pointer"
                      onChange={(e) => handleUploadPicture(e, proof)}
                      name="delivery_receipt_file"
                      accept="image/*"
                      disabled={['COMPLETED', 'DELIVERED'].includes(row.status)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )
    }
    return (
      <>
        <InformationTransaction data={row} />
        <hr className="m-0" />
        <div className="row my-3">
          <div className="col-12 my-2">
            <strong className="font-size-18 p-2">{t(`title.bill_list`)}</strong>
          </div>
        </div>
        <table className="table table-responsive table-borderless table-scrollable">
          <thead className="border-bottom">
            <tr>
              <th className="col-2">{t('title.bill.invoice_code')}</th>
              <th className="col-2">{t('title.bill.due_date')}</th>
              <th className="col-2">{t('title.bill.total_amount')}</th>
              <th className="col-2">{t('title.bill.status')}</th>
              <th className="col-2">{t('title.bill.payment_method')}</th>
              <th className="col-2">{t('title.bill.invoice_adaloka')}</th>
              <th className="col-2">{t('title.bill.bill_file')}</th>
              <th className="col-2">{t('title.bill.tax_invoice_file')}</th>
              {bill[0]?.payment_method === 'PAYMENT_GATEWAY' ? (
                <th className="col-2">#</th>
              ) : (
                <th className="col-2">{t('title.bill.transfer_evidence_file')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            <OrderInstallment data={bill} header={row} />
          </tbody>
        </table>
        <hr className="m-0" />
        <table className="table table-responsive table-borderless my-3">
          <thead className="border-bottom">
            <tr>
              <th className="col-4">{t('title.product.title')}</th>
              <th className="col-2">{t('title.product.price')}</th>
              <th className="col-2">{t('title.product.qty')}</th>
              <th className="col-2">{t('title.product.weight_total')}</th>
              <th className="col-2">{t('title.product.price_total')}</th>
              <th className=""></th>
            </tr>
          </thead>
          <tbody>
            {items?.map((item, key) => {
              subtotal += item.product_quantity * item.product_price
              subweight += item.product_weight * item.product_quantity
              return (
                <tr key={key}>
                  <td className="col d-flex flex-column flex-md-row">
                    <div className="col-8 col-md-3 min-width-60 px-0">
                      <Link
                        to={`/product/detail/${
                          prettyUrl(item.product_title) + '/' + item.product_uid
                        }`}
                      >
                        <img src={item.product_thumbnail} className="img-fluid" alt="" />
                      </Link>
                    </div>
                    <div className="col-12 col-md-9 px-0 px-md-3">
                      <Link
                        to={`/product/detail/${
                          prettyUrl(item.product_title) + '/' + item.product_uid
                        }`}
                        className="text-black product-title-ellipsis"
                        title={item.product_title}
                      >
                        <span>
                          <strong>{item.product_title}</strong>
                        </span>
                      </Link>
                    </div>
                  </td>
                  <td>
                    {currencyFormatDecimal(item.product_price, item.product_price_currency)} /{' '}
                    <small>{item.product_unit_type}</small>
                  </td>
                  <td>{formatDecimal(item.product_quantity, 2)}</td>
                  <td>
                    {`${numberFormatAPI(item.product_weight * item.product_quantity, {
                      leadingZeroAllowed: true,
                      zeroDecimal: true,
                      amountZeroDecimal: 2,
                    })} ${t('title.product.weight_kg')}`}
                  </td>
                  <td>
                    {currencyFormatDecimal(
                      item.product_price * item.product_quantity,
                      item.product_price_currency,
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {/* {['PROCESSED', 'WAITING_PAYMENT'].includes(row.status) &&
          row.delivery_type === 'PICKUP' && <DeliveryProof />} */}
        {row.status !== 'CANCELLED' && (
          <>
            <hr className="mt-0 mb-3" />
            <div className="row mb-3 form-group">
              <div className="col-sm-2">
                <label className="align-items-md-left font-size-12 font-weight-bold col-form-label">
                  {t('form.delivery_proof')}
                </label>
              </div>
              {Object.keys(deliveryReceiptFile).map((item) => (
                <DeliveryProof key={item} proof={item} />
              ))}
              <div className="col-sm-12 mt-2">
                <div className="content-profile ml-3 mt-0 mt-md-4">
                  {error?.delivery_proof && (
                    <div className="text-danger">{error.delivery_proof}</div>
                  )}
                </div>
                <div>
                  <span className="font-size-12">
                    {t('form.image_size')}
                    <br />
                    {t('form.image_type')}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        <hr className="m-0" />
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <table className="table table-borderless m-2">
              <tbody>
                <tr>
                  <td className="col-xs-6 p-0">{t('title.subtotal_product')}</td>
                  <td className="p-0">
                    <div className="font-weight-bold">
                      {currencyFormatDecimal(subtotal, items[0]?.product_price_currency)}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-xs-6 p-0">{t('title.subtotal_weight')}</td>
                  <td className="p-0">
                    <div className="font-weight-bold">{`${numberFormatAPI(subweight, {
                      leadingZeroAllowed: true,
                      zeroDecimal: true,
                      amountZeroDecimal: 2,
                    })} ${t('title.product.weight_kg')}`}</div>
                  </td>
                </tr>
                <tr>
                  <td className="p-0">{t('title.delivery_cost')}</td>
                  <td className="p-0">
                    <div className="font-weight-bold">
                      {currencyFormat(row?.delivery_cost, row?.delivery_cost_currency)}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="p-0">{t('title.delivery_insurance_cost')}</td>
                  <td className="p-0">
                    <div className="font-weight-bold">
                      {currencyFormat(
                        row?.delivery_insurance_cost,
                        row?.delivery_insurance_currency,
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="col-xs-12 col-md-6 d-flex text-center justify-content-center justify-content-md-end mb-2">
            <div className="mt-2">
              <span className="font-size-16 font-weight-bold text-grey">
                {t('title.total_transaction')}
              </span>
              <br />
              <span className="font-size-20 font-weight-bold text-dark">
                {currencyFormatDecimal(row?.payment_amount, row?.payment_amount_currency)}
              </span>
            </div>
          </div>
        </div>
      </>
    )
  }

  const CardFooter = (data) => {
    const row = data?.data
    const createdOrderTime = moment(row.created_date)
    const configTime = moment(INVOICE_V2)
    const checkIfBillInvoiceOnlyDraft = (data) => {
      if (data.bill) {
        let billData = JSON.parse(data.bill)
        for (let index = 0; index < billData.length; index++) {
          if (billData[index].status !== 'DRAFT' && billData[index].status !== 'CANCELLED') {
            return false
          }
        }
      }
      return true
    }

    const checkCompletedInvoice = (data) => {
      var paymentOrder = data.payment_amount
      let paymentPaid = 0
      let usedReturnSaldo = 0
      if (data.bill) {
        let billData = JSON.parse(data.bill)
        for (let index = 0; index < billData.length; index++) {
          if (billData[index].status === 'PAID') {
            paymentPaid += billData[index].origin_amount
            usedReturnSaldo += billData[index].retur_amount
          }
        }
      }

      const totalPaid = paymentPaid + usedReturnSaldo
      if (totalPaid >= paymentOrder) {
        return true
      }
      return false
    }

    return (
      row && (
        <>
          <hr className="m-0" />
          {/* <AlertBill row={row} /> */}
          <div className="row overflow-auto">
            <div className="col-12 mt-2 mb-2 d-flex flex-column flex-md-row justify-content-center justify-content-md-end">
              <div
                onClick={() => handleDownloadPdf(row)}
                className="d-inline btn btn-warning btn-xs m-2 font-size-10 pointer"
              >
                <i className="far fa-file"></i> {t('button.download_pdf')}
              </div>
              {!['CANCELLED', 'REFUND'].includes(row?.status) && (
                <Link
                  to={'#'}
                  onClick={() => {
                    window.location = `/chat?order=${row?.uid}&seller=${row?.store_uid}`
                  }}
                  className="d-inline btn btn-red btn-xs m-2 font-size-10"
                >
                  <i className="fa fa-comments"></i> {t('button.chat_seller')}
                </Link>
              )}
              {/* {row.status === 'WAITING_PAYMENT' &&
                row?.payment_type === 'FULL_PAYMENT' &&
                row?.payment_status === 'UNPAID' && (
                  <>
                    <div
                      onClick={() => handleTransactionUpdate(row, 'CANCELLED')}
                      className="d-inline btn btn-dark btn-xs m-2 font-size-10 pointer"
                    >
                      <i className="fa fa-ban"></i> {t('button.cancelled')}
                    </div>
                  </>
                )} */}
              {createdOrderTime.isBefore(configTime) &&
                ['PROCESSED', 'WAITING_PAYMENT'].includes(row.status) &&
                row.delivery_type === 'PICKUP' && (
                  <>
                    <Link
                      to={'#'}
                      onClick={() => handleTransactionUpdate(row, 'DELIVERED')}
                      className="d-inline btn btn-success btn-xs m-2 font-size-10"
                      id="complete-order"
                    >
                      <i className="fa fa-check"></i> {t('button.received_order')}
                    </Link>
                  </>
                )}
              {createdOrderTime.isBefore(configTime) && row.status === 'SHIPPED' && (
                <>
                  <Link
                    to={'#'}
                    onClick={() => handleTransactionUpdate(row, 'DELIVERED')}
                    className="d-inline btn btn-success btn-xs m-2 font-size-10"
                    id="complete-order"
                  >
                    <i className="fa fa-check"></i> {t('button.received_order')}
                  </Link>
                </>
              )}
              {['DELIVERED'].includes(row?.status) &&
                checkCompletedInvoice(row) &&
                row?.count_shipping_unpaid_bill === 0 && (
                  <>
                    <Link
                      to={'#'}
                      onClick={() => handleTransactionUpdate(row, 'COMPLETED')}
                      className="d-inline btn btn-success btn-xs m-2 font-size-10"
                    >
                      <i className="fa fa-check"></i> {t('button.finish_order')}
                    </Link>
                  </>
                )}
              {row.status === 'PROCESSED' &&
                checkIfBillInvoiceOnlyDraft(row) &&
                !isAnyRequestCorrection && (
                  <>
                    <div
                      onClick={() => handleTransactionUpdate(row, 'CANCELLED')}
                      className="d-inline btn btn-dark btn-xs m-2 font-size-10 pointer"
                    >
                      <i className="fa fa-ban"></i> {t('button.cancelled')}
                    </div>
                  </>
                )}
              {['SHIPPED', 'DELIVERED'].includes(row?.status) &&
                (!checkCompletedInvoice(row) ||
                  (checkCompletedInvoice(row) && row?.count_shipping_unpaid_bill > 0)) && (
                  <button
                    onClick={onCloseOrder}
                    className="d-inline btn btn-dark btn-xs m-2 font-size-10 pointer"
                  >
                    <i className="fa fa-ban"></i> {t('button.close_order')}
                  </button>
                )}
            </div>
          </div>
        </>
      )
    )
  }

  const InformationTransaction = (data) => {
    const row = data.data
    return (
      <div className="row my-3 text-break">
        <div className="col-12 my-2">
          <strong className="font-size-18 p-2">{t(`title.order_information`)}</strong>
        </div>
        <div className="col-xs-12 col-md-3">
          <div className="p-2">
            <small>{t(`title.purchase_request_number`)}</small> <br />
            <strong>{row?.purchase_request_number ? row?.purchase_request_number : '-'}</strong>
            {!['CANCELLED', 'COMPLETED', 'REFUND'].includes(row?.status) && (
              <div
                role="button"
                onClick={() => {
                  setModalPrOpen(true)
                }}
                className="ml-2 fas fa-edit btn-link border-0 bg-transparent pointer"
              ></div>
            )}
            <Modal open={modalPrOpen} onClose={handleModalPr}>
              <OrderPurchaseNumberForm
                data={row}
                handleModal={handleModalPr}
                initialPurchaseNumber="PR"
              />
            </Modal>
          </div>
        </div>
        <div className="col-xs-12 col-md-3">
          <div className="p-2">
            <small>{t(`title.purchase_order_number`)}</small> <br />
            <strong>{row.purchase_order_number ? row.purchase_order_number : '-'}</strong>
            {!['CANCELLED', 'COMPLETED', 'REFUND'].includes(row.status) && (
              <div
                role="button"
                onClick={() => {
                  setModalPoOpen(true)
                }}
                className="ml-2 fas fa-edit btn-link border-0 bg-transparent pointer"
              ></div>
            )}
            <Modal open={modalPoOpen} onClose={handleModalPo}>
              <OrderPurchaseNumberForm
                data={row}
                handleModal={handleModalPo}
                initialPurchaseNumber="PO"
              />
            </Modal>
          </div>
        </div>
        <div className="col-xs-12 col-md-3">
          <div className="p-2">
            <small>{t(`title.seller_name`)}</small>
            <br />
            <strong>{row?.store_name}</strong>
          </div>
        </div>
        <div className="col-xs-12 col-md-3">
          <div className="p-2">
            <small>{t(`title.ship_consignee`)}</small>
            <br />
            <strong>{`${row?.consignee_full_name} (${row?.consignee_phone_number})`}</strong>
          </div>
        </div>
        <div className="col-xs-12 col-md-3">
          <div className="p-2">
            <small>{t(`title.order_date`)}</small>
            <br />
            <strong>{formatDate(row?.created_date, 'DD/MM/YYYY HH:mm')}</strong>
          </div>
        </div>
        <div className="col-xs-12 col-md-3">
          <div className="p-2">
            <small>{t(`title.payment_status`)}</small>
            <br />
            <strong>{t(`title.${row?.payment_status.toLowerCase()}`)}</strong>
          </div>
        </div>
        <div className="col-xs-12 col-md-3">
          <div className="p-2">
            <small>{t(`invoice.payment_method`)}</small>
            <br />
            <strong>{t(`initial_status.${row?.payment_type}`)}</strong>
          </div>
        </div>
        <div className="col-xs-12 col-md-3">
          <div className="p-2">
            <small>{t(`title.delivery_type`)}</small>
            <br />
            <strong>{t(`initial_status.${row?.delivery_type}`)}</strong>
          </div>
        </div>
        {row?.delivery_estimated_time && (
          <div className="col-xs-12 col-md-3">
            <div className="p-2">
              <small>
                {row?.delivery_type === 'PICKUP'
                  ? t(`title.delivery_date_pickup`)
                  : t(`title.delivery_date_ship`)}
              </small>
              <br />
              <strong>{formatDate(row?.delivery_estimated_time, 'DD/MM/YYYY HH:mm')}</strong>
            </div>
          </div>
        )}
        {/* <div className="col-xs-12 col-md-3">
          <div className="p-2">
            <small>{t(`title.payment_status`)}</small>
            <br />
            <strong>{row.payment_status === 'PAID' ? t(`title.paid`) : t(`title.unpaid`)}</strong>
          </div>
        </div> */}
        {row?.status === 'CANCELLED' && (
          <div className="col-xs-12 col-md-3">
            <div className="p-2">
              <small>{t('title.rejected_reason')}</small>
              <br />
              <strong>{row?.reason_cancel_reject}</strong>
            </div>
          </div>
        )}
        <div className="col-xs-12 col-md-8">
          <div className="p-2">
            <small>
              {row?.delivery_type === 'PICKUP' ? t(`title.pickup_at`) : t(`title.ship_to`)}
            </small>
            <br />
            <strong>{row?.destination_address}</strong>
          </div>
        </div>
      </div>
    )
  }

  const OrderInstallment = (bill) => {
    return (
      <>
        {bill?.data?.length > 0 ? (
          bill.data.map((data, index) => {
            return (
              <tr key={index}>
                <td>
                  {data.invoice_code} <br />
                  <small>{data.note}</small>
                </td>
                <td>{formatDate(data.due_date, 'DD/MM/YYYY HH:mm')}</td>
                <td>{currencyFormat(data.total_amount, 'IDR')}</td>
                <td>
                  <div className="d-flex align-items-center">
                    <div
                      className={`status-indicator m-2 ${
                        ['ISSUED', 'PENDING_APPROVAL'].includes(data.status) ? 'status-pending' : ''
                      }${['PAID'].includes(data.status) ? 'status-success' : ''}${
                        ['REJECTED', 'CANCELLED'].includes(data.status) ? 'status-reject' : ''
                      }`}
                    ></div>
                    {t(`status.bill.${data.status}`)}
                  </div>
                </td>
                <td>{t(`title.payment_method.${data.payment_method.toLowerCase()}`)}</td>
                <td>
                  <div className="lightbox">
                    {['ISSUED', 'PROCESSED', 'PAID'].includes(data.status) ? (
                      <div
                        onClick={() =>
                          handleDownloadInvoice({
                            ...data,
                            purchase_order_number: bill?.header?.purchase_order_number,
                            purchase_request_number: bill?.header?.purchase_request_number,
                          })
                        }
                        className="d-inline btn btn-primary btn-xs m-2 font-size-10 pointer"
                      >
                        <i className="fa fa-download"></i> {t('button.download_invoice_adaloka')}
                      </div>
                    ) : (
                      <small className="text-grey">{t('title.no_data')}</small>
                    )}
                  </div>
                </td>
                <td>
                  <div className="lightbox">
                    {data.payment_method === 'MANUAL_TRANSFER' && data.file_invoice ? (
                      ['jpg', 'jpeg', 'png'].includes(
                        FindFileType(data.file_invoice.toLowerCase()),
                      ) ? (
                        <ImageModal title={t('title.bill.bill_file')} src={data.file_invoice} />
                      ) : (
                        <a href={data.file_invoice} target={'_blank'} rel="noreferrer" download>
                          <img
                            className="form-picture-image"
                            src={pdfLogo}
                            width="60"
                            height="60"
                            alt=""
                          />
                        </a>
                      )
                    ) : (
                      <small className="text-grey">{t('title.no_data')}</small>
                    )}
                  </div>
                </td>
                <td>
                  <div className="lightbox">
                    {data.file_tax ? (
                      ['jpg', 'jpeg', 'png'].includes(FindFileType(data.file_tax.toLowerCase())) ? (
                        <ImageModal title={t('title.bill.bill_file')} src={data.file_tax} />
                      ) : (
                        <a href={data.file_tax} target={'_blank'} rel="noreferrer" download>
                          <img
                            className="form-picture-image"
                            src={pdfLogo}
                            width="60"
                            height="60"
                            alt=""
                          />
                        </a>
                      )
                    ) : (
                      <small className="text-grey">{t('title.no_data')}</small>
                    )}
                  </div>
                </td>
                <td>
                  {['PAYMENT_GATEWAY', 'CREDIT_CARD'].includes(data.payment_method) ? (
                    <>
                      {['ISSUED'].includes(data.status) &&
                      data.payment_invoice_url !== '' &&
                      bill?.header?.status !== 'CANCELLED' ? (
                        <div>
                          {/* existing: redirect to xendit invoice url, replaced by bank transfer popup
                          <div
                            onClick={() => {
                              window.location = data.payment_invoice_url
                            }}
                            className="d-inline btn btn-warning btn-xs m-2 font-size-10 pointer"
                          >
                            <i className="mr-1 far fa-id-card"></i> {t('button.payment_process')}
                          </div>
                          */}
                          <div
                            onClick={() => {
                              setModalBankTransferOpen(true)
                              setModalBankTransferData(data)
                            }}
                            className="d-inline btn btn-warning btn-xs m-2 font-size-10 pointer"
                          >
                            <i className="mr-1 far fa-id-card"></i> {t('button.payment_process')}
                          </div>
                          <div
                            onClick={() => {
                              setModalReturOpen(true)
                              setModalReturnData(data)
                            }}
                            className="d-inline btn btn-danger btn-xs m-2 font-size-10 pointer"
                            style={{ backgroundColor: '#469a84', borderColor: '#469a84' }}
                          >
                            <i className="mr-1 fas fa-undo-alt"></i> {t('button.apply_return')}
                          </div>
                        </div>
                      ) : (
                        <>-</>
                      )}
                    </>
                  ) : (
                    // METHOD MANUAL TRANSFER
                    <>
                      <div className="d-flex align-items-center">
                        {data.file_proof_payment !== '' &&
                          (['jpg', 'png', 'jpeg'].includes(
                            FindFileType(data.file_proof_payment.toLowerCase()),
                          ) ? (
                            <ImageModal
                              title={t('title.bill.transfer_evidence_file')}
                              src={data.file_proof_payment}
                              border={error?.file_proof_payment}
                            />
                          ) : (
                            <a
                              href={data.file_proof_payment}
                              target={'_blank'}
                              rel="noreferrer"
                              download
                            >
                              <img
                                className="form-picture-image"
                                src={pdfLogo}
                                width="60"
                                height="60"
                                alt=""
                              />
                            </a>
                          ))}
                        {data.status !== 'PAID' && bill?.header?.status !== 'CANCELLED' && (
                          <div
                            className={`btn btn-xs pointer position-relative mt-2 ${
                              data?.file_proof_payment
                                ? 'font-weight-semi-bold font-size-12 btn-link'
                                : 'btn-outline-primary'
                            }`}
                          >
                            {data.file_proof_payment !== ''
                              ? t('title.update')
                              : t('button.upload')}
                            <input
                              type="file"
                              className="input-file pointer"
                              onChange={(e) => {
                                handleUploadFile(e, data)
                              }}
                              name="file_proof_payment"
                              accept="image/jpg,image/jpeg,image/png,.pdf"
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </td>

                {modalReturOpen && (
                  <Modal
                    open={modalReturOpen}
                    onClose={() => {
                      setModalReturOpen(false)
                      setModalReturnData({})
                    }}
                    classNames={{
                      modal: 'w-100 w-xl-35 h-auto',
                      modalContainer: 'row justify-content-center align-items-center',
                    }}
                  >
                    <WideCardOrderReturn bill={modalReturnData} />
                  </Modal>
                )}

                {modalBankTransferOpen && (
                  <Modal
                    open={modalBankTransferOpen}
                    showCloseIcon={false}
                    onClose={() => {
                      setModalBankTransferOpen(false)
                      setModalBankTransferData({})
                    }}
                    classNames={{
                      modal: 'w-100 w-xl-35 h-auto',
                      modalContainer: 'row justify-content-center align-items-center',
                    }}
                  >
                    <WideCardBankTransferPayment
                      data={modalBankTransferData}
                      onClose={() => {
                        setModalBankTransferOpen(false)
                        setModalBankTransferData({})
                      }}
                    />
                  </Modal>
                )}
              </tr>
            )
          })
        ) : (
          <tr>
            <td colSpan="8" className="text-center">
              {t('title.no_data')}
            </td>
          </tr>
        )}
        <Modal
          open={isModalRejected}
          onClose={() => setIsModalRejected(false)}
          classNames={{
            modal: 'w-100 w-xl-35 h-auto',
            modalContainer: 'row justify-content-center align-items-center',
          }}
        >
          <WirecardCancelForm
            data={payloadRejected}
            handleSubmit={handleOrderRejected}
            handleModal={() => setIsModalRejected(false)}
          />
        </Modal>
      </>
    )
  }

  return (
    <>
      {loading ? (
        <div className="d-flex justify-content-center">
          <Loading />
        </div>
      ) : (
        <>
          {datacard?.items?.length > 0 ? (
            <>
              {datacard?.items?.map((row, key) => {
                return (
                  <div key={key}>
                    <div className="border border-width-2 border-color-3 borders-radius-6 container wide-card rounded mb-5">
                      <CardHeader data={row} />
                      <div className="row">
                        <div className="col-12">
                          <CardBody data={row} />
                        </div>
                      </div>
                      <CardFooter data={row} />
                    </div>
                  </div>
                )
              })}
            </>
          ) : (
            <>
              <div className="d-flex justify-content-center">{t('message.data_not_found')}</div>
            </>
          )}
        </>
      )}
    </>
  )
}

export default WideCardOrder
